import { Trans, useLingui } from "@lingui/react/macro";
import { Alert, Skeleton } from "@offstreetparking/design-system/feedback";
import { Heading, Link } from "@offstreetparking/design-system/typography";
import { useEffect, useRef } from "react";
import * as z from "zod/v4";
import type { RouterOutputs } from "../../../../api/types";
import { routes } from "../../../../routes";
import { Form } from "../../../forms/Form";
import { WysiwygView } from "../../../forms/Wysiwyg/WysiwygView";
import { SummaryContent } from "../Summary/SummaryContent";
type TermItem =
	| RouterOutputs["parkerTerms"]["getParkerTermsForPermitType"]["items"][number]
	| RouterOutputs["parkerTerms"]["getAcceptedParkerTermsVersions"]["items"][number];
const schema = z.object({
	parkerTermsVersionIds: z.array(z.number()).min(1),
});
type TermsFormProps = {
	formId: string;
	currentTerms?: TermItem;
	isPending?: boolean;
	acceptedTerms?: TermItem[];
	onAccept: (data: {
		parkerTermsVersionIds: number[];
	}) => void;
};
export const TermsForm = ({ formId, currentTerms, isPending, acceptedTerms = [], onAccept }: TermsFormProps) => {
	const ref = useRef<HTMLDivElement>(null);
	const { t } = useLingui();
	useEffect(() => {
		if (currentTerms?.parkerTermsVersionId && ref.current) {
			ref.current.scrollTop = 0;
		}
	}, [currentTerms?.parkerTermsVersionId]);
	if (isPending) {
		return (
			<div className="space-y-4">
				<Skeleton className="h-8 w-[50%]" />
				{Array.from({
					length: 5,
				}).map((_, index) => (
					// biome-ignore lint/suspicious/noArrayIndexKey: This is a static skeleton, not a list of dynamic items
					<Skeleton key={`skeleton-${index}`} className="h-5 w-full" />
				))}
			</div>
		);
	}
	const hasFailedToLoadTerms = !currentTerms && !acceptedTerms?.length;
	if (hasFailedToLoadTerms) {
		return (
			<Form
				schema={schema}
				id={formId}
				initialValues={{
					parkerTermsVersionIds: acceptedTerms.map((a) => a.parkerTermsVersionId),
				}}
				className="flex h-full flex-col justify-between"
				onSubmit={(data) => {
					const parkerTermsVersionIds = data.parkerTermsVersionIds;
					onAccept({
						parkerTermsVersionIds,
					});
				}}
			>
				<Alert
					type={"error"}
					title={t`Something went wrong while loading terms. Please try again or contact support if the issue persists.`}
				/>
			</Form>
		);
	}
	const hasAcceptedAllTerms = !currentTerms;
	if (hasAcceptedAllTerms) {
		return (
			<Form
				schema={schema}
				id={formId}
				initialValues={{
					parkerTermsVersionIds: acceptedTerms.map((a) => a.parkerTermsVersionId),
				}}
				className="flex h-full flex-col justify-between"
				onSubmit={(data) => {
					const parkerTermsVersionIds = data.parkerTermsVersionIds;
					onAccept({
						parkerTermsVersionIds,
					});
				}}
			>
				<div className="space-y-6">
					{acceptedTerms.map((terms, index) => (
						<SummaryContent
							key={terms.parkerTermsVersionId}
							title={terms.title || t`Terms #${String(index + 1)}`}
						>
							<Trans>
								Terms accepted.{" "}
								<Link href={routes.terms(terms)} isExternal>
									View here
								</Link>
								.
							</Trans>
						</SummaryContent>
					))}
				</div>
			</Form>
		);
	}
	return (
		<Form
			schema={schema}
			id={formId}
			initialValues={{
				parkerTermsVersionIds: [
					...acceptedTerms.map((a) => a.parkerTermsVersionId),
					currentTerms?.parkerTermsVersionId,
				].filter(Boolean),
			}}
			className="flex h-full flex-col justify-between"
			onSubmit={(data) => {
				const parkerTermsVersionIds = data.parkerTermsVersionIds;
				onAccept({
					parkerTermsVersionIds,
				});
			}}
		>
			<div className="space-y-6">
				<Heading>{currentTerms.title || t`Terms #${String(acceptedTerms.length + 1)}`}</Heading>
				<div ref={ref} className="relative pb-16 md:max-h-[40vh] md:overflow-y-scroll">
					<WysiwygView
						content={"content" in currentTerms ? currentTerms.content : ""}
						options={{
							allowHeadings: true,
						}}
					/>
					<div
						aria-hidden="true"
						className="-bottom-16 pointer-events-none left-0 hidden h-16 w-full bg-gradient-to-t from-white to-transparent md:sticky md:block"
					/>
				</div>
			</div>
		</Form>
	);
};
